import { useState } from "react";
import { Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

export const UpgradeToCompanyButton = ({
  customerId,
  customerName,
  label = "Upgrade to Company",
  onDone,
  testId,
  className = "",
  selfServe = false,
}) => {
  const [busy, setBusy] = useState(false);

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/customers/${customerId}/upgrade?origin_url=${encodeURIComponent(window.location.origin)}`
      );
      toast.success(`${data.company_name} now has its own Vendor/Supplier Portal workspace.`);
      await onDone?.(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant={selfServe ? "default" : "outline"}
          size="sm"
          data-testid={`${testId}-open`}
          className={
            selfServe
              ? `rounded-full bg-blue-600 px-5 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95 ${className}`
              : `rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100 ${className}`
          }
        >
          <Rocket className="mr-1.5 h-3.5 w-3.5" /> {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-xl font-extrabold tracking-tight">
            {selfServe ? "Start your own workspace?" : `Give ${customerName} a workspace?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {selfServe
              ? "You will get a Vendor/Supplier Portal alongside this one, using the same login — your business name, contact and phone are carried over. You can add your own customers straight away and pick a plan whenever you are ready."
              : `${customerName} will get their own Vendor/Supplier Portal on the same login, with their company name, contact and phone carried over. Their existing references stay exactly as they are.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={`${testId}-cancel`}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={upgrade}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className="rounded-full bg-blue-600 font-semibold hover:bg-blue-700"
          >
            {busy ? "Creating…" : "Create workspace"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
